import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { MetricCard } from "@/components/MetricCard";
import { Battery, Sun, Zap, Clock, AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "";
  }
}

function RuntimeEstimateCard({ estimate }) {
  const status = estimate?.status || "no_pzem";
  const isOk = status === "ok";
  const soc = estimate?.min_soc_pct;
  // 40 % → user's business rule: charge urgently before running dry.
  const lowBattery = typeof soc === "number" && soc < 40;

  const bigValue = isOk
    ? `${estimate.remaining_hours_int}j ${estimate.remaining_minutes_int}m`
    : status === "no_load"
    ? "Tidak ada beban"
    : status === "no_pzem"
    ? "Belum ada data PZEM"
    : status === "invalid_capacity"
    ? "Kapasitas baterai belum diatur"
    : "—";

  const hint = isOk
    ? `SoC min ${(soc ?? 0).toFixed(1)}% · Beban ${(estimate.total_power_w ?? 0).toFixed(1)} W · Kapasitas ${(estimate.battery_capacity_wh ?? 0).toFixed(0)} Wh`
    : status === "no_load"
    ? `Daya terukur ≤ 1 W · SoC min ${(soc ?? 0).toFixed(1)}%`
    : status === "invalid_capacity"
    ? "Atur kapasitas baterai (Wh) di pengaturan proyek."
    : "Tambahkan/aktifkan sensor PZEM untuk mulai estimasi.";

  // Accent color: red when < 40 %, primary when OK, muted otherwise.
  const accent = lowBattery
    ? "text-destructive"
    : isOk
    ? "text-primary"
    : "text-muted-foreground";

  return (
    <>
      {lowBattery && (
        <div
          data-testid="energy-low-battery-alert"
          className="mb-4 border border-destructive/40 bg-destructive/5 text-destructive rounded-lg p-4 flex items-start gap-3"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-semibold text-sm">
              Peringatan: Baterai perlu di-charge segera
            </div>
            <div className="text-xs mt-1 opacity-90">
              State of Charge saat ini <span className="font-semibold">{soc.toFixed(1)}%</span> (di bawah ambang 40%). Kurangi beban irigasi non-esensial dan pastikan panel surya menghasilkan daya, atau siapkan pengisian cadangan.
            </div>
          </div>
        </div>
      )}
      <div
        data-testid="energy-runtime-estimate-card"
        className={`mb-6 border rounded-lg p-6 flex items-start gap-4 ${
          lowBattery ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"
        }`}
      >
        <Clock className={`w-6 h-6 mt-1 ${accent}`} />
        <div className="flex-1">
          <div className="overline text-muted-foreground">Estimasi Operasional Tersisa</div>
          <div
            data-testid="energy-runtime-estimate-value"
            className={`font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 ${accent}`}
          >
            {bigValue}
          </div>
          <div
            data-testid="energy-runtime-estimate-hint"
            className="mt-2 text-xs text-muted-foreground"
          >
            {hint}
          </div>
          {estimate?.battery_voltage_min !== undefined && estimate?.battery_voltage_max !== undefined && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              Kalibrasi VRLA: {(estimate.battery_voltage_min ?? 0).toFixed(2)} V (0%) → {(estimate.battery_voltage_max ?? 0).toFixed(2)} V (100%)
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function Energy() {
  const { currentId, current } = useProject();
  const [readings, setReadings] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [latestSoc, setLatestSoc] = useState(0);
  const [estimate, setEstimate] = useState(null);

  // VRLA calibration — prefer estimate response (authoritative from backend), fallback
  // to project setting, then hardcoded VRLA default (11.4–13.5 V).
  const vMin = estimate?.battery_voltage_min ?? current?.battery_voltage_min ?? 11.4;
  const vMax = estimate?.battery_voltage_max ?? current?.battery_voltage_max ?? 13.5;
  const vSpan = Math.max(0.01, vMax - vMin);
  // Recompute SoC from a reading's `metrics.voltage` — this corrects legacy readings whose
  // stored `.value` still uses the old energy_wh-based formula.
  const socFromReading = (r) => {
    const v = r?.metrics?.voltage;
    if (typeof v === "number" && !Number.isNaN(v)) {
      return Math.max(0, Math.min(100, ((v - vMin) / vSpan) * 100));
    }
    // No voltage in metrics (simulator/legacy) → fall back to stored value.
    return Number(r?.value) || 0;
  };

  const fetch_ = async () => {
    if (!currentId) return;
    const [{ data: rData }, { data: sData }, estRes] = await Promise.all([
      api.get(`/projects/${currentId}/readings`, { params: { limit: 400 } }),
      api.get(`/projects/${currentId}/sensors`),
      api.get(`/projects/${currentId}/energy/estimate`).catch(() => ({ data: null })),
    ]);
    setReadings(rData);
    setSensors(sData);
    setEstimate(estRes?.data || null);
  };

  useEffect(() => {
    fetch_();
    const id = setInterval(fetch_, 5000);
    return () => clearInterval(id);
  }, [currentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pzemSensors = useMemo(() => sensors.filter((s) => s.type === "pzem"), [sensors]);

  // Latest SoC per PZEM sensor → average — computed AFTER voltage-based re-derivation so
  // legacy readings (stored with old energy-based SoC) are corrected on the client.
  useEffect(() => {
    const pzemVals = readings.filter((r) => r.sensor_type === "pzem");
    if (!pzemVals.length) {
      setLatestSoc(0);
      return;
    }
    const perSensor = {};
    for (const r of pzemVals) {
      if (!(r.sensor_id in perSensor)) perSensor[r.sensor_id] = socFromReading(r);
    }
    const vals = Object.values(perSensor);
    setLatestSoc(vals.reduce((a, b) => a + b, 0) / vals.length);
  }, [readings, vMin, vMax]); // eslint-disable-line react-hooks/exhaustive-deps

  const trend = useMemo(() => {
    // Multi-sensor: bucket by time, key by sensor_id
    const buckets = new Map();
    for (const r of readings) {
      if (r.sensor_type !== "pzem") continue;
      const t = fmtTime(r.timestamp);
      if (!buckets.has(t)) buckets.set(t, { time: t });
      buckets.get(t)[r.sensor_id] = socFromReading(r);
    }
    // readings come DESC (newest first) from backend → slice HEAD for newest N, then reverse for LTR chart.
    return Array.from(buckets.values()).slice(0, 40).reverse();
  }, [readings, vMin, vMax]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract latest PZEM metrics — real V/A/W/Wh from ESP32 push (fallback to 0 if none).
  // `readings` sorted DESC, so first match per sensor_id = newest.
  const pzemLatestMetrics = useMemo(() => {
    const perSensor = new Map();
    for (const r of readings) {
      if (r.sensor_type !== "pzem") continue;
      if (perSensor.has(r.sensor_id)) continue;
      perSensor.set(r.sensor_id, r);
    }
    return Array.from(perSensor.values());
  }, [readings]);

  const aggregate = useMemo(() => {
    if (!pzemLatestMetrics.length) return { voltage: 0, current: 0, power: 0, energy_wh: 0, soc: 0 };
    const sum = pzemLatestMetrics.reduce(
      (acc, r) => {
        const m = r.metrics || {};
        acc.voltage += Number(m.voltage) || 0;
        acc.current += Number(m.current) || 0;
        acc.power += Number(m.power) || 0;
        acc.energy_wh += Number(m.energy_wh) || 0;
        acc.soc += Number(r.value) || 0;
        return acc;
      },
      { voltage: 0, current: 0, power: 0, energy_wh: 0, soc: 0 },
    );
    // Voltage & current typically SAME across parallel panels; show average not sum.
    // Power & energy are additive across independent panels.
    const n = pzemLatestMetrics.length;
    return {
      voltage: sum.voltage / n,
      current: sum.current / n,
      power: sum.power,
      energy_wh: sum.energy_wh,
      soc: sum.soc / n,
    };
  }, [pzemLatestMetrics]);

  useEffect(() => {
    setLatestSoc(aggregate.soc);
  }, [aggregate.soc]);

  if (!currentId) return <div className="text-muted-foreground">Pilih proyek terlebih dulu.</div>;

  const radialData = [{ name: "soc", value: latestSoc, fill: "hsl(var(--primary))" }];

  return (
    <div>
      <PageHeader
        title="Monitoring Energi Surya"
        overline={current?.name}
        description="State of Charge baterai panel surya, daya, dan total energi."
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6" data-testid="energy-metric-row">
        <MetricCard overline="State of Charge" value={latestSoc.toFixed(1)} unit="%" icon={Battery} testId="energy-metric-soc" />
        <MetricCard overline="Tegangan" value={aggregate.voltage.toFixed(1)} unit="V" icon={Zap} testId="energy-metric-voltage" />
        <MetricCard overline="Arus" value={aggregate.current.toFixed(2)} unit="A" icon={Zap} testId="energy-metric-current" />
        <MetricCard overline="Daya Instan" value={aggregate.power.toFixed(1)} unit="W" icon={Zap} accent="accent" testId="energy-metric-power" />
        <MetricCard overline="Total Energi" value={(aggregate.energy_wh / 1000).toFixed(2)} unit="kWh" icon={Sun} testId="energy-metric-energy" />
      </div>

      <RuntimeEstimateCard estimate={estimate} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-border rounded-lg bg-card p-6">
          <div className="overline text-muted-foreground">Gauge SoC</div>
          <h3 className="font-display text-lg font-semibold mt-1 mb-4">Kondisi Baterai</h3>
          <ResponsiveContainer width="100%" height={260}>
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={radialData} startAngle={210} endAngle={-30}>
              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
              <RadialBar background dataKey="value" cornerRadius={8} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="-mt-32 text-center pointer-events-none">
            <div className="font-display text-4xl font-bold">{latestSoc.toFixed(0)}%</div>
            <div className="text-xs text-muted-foreground mt-1">PZEM Solar</div>
          </div>
        </div>

        <div className="lg:col-span-2 border border-border rounded-lg bg-card p-6">
          <div className="overline text-muted-foreground">Trend</div>
          <h3 className="font-display text-lg font-semibold mt-1 mb-4">
            SoC over time {pzemSensors.length > 0 && <span className="text-sm text-muted-foreground font-normal">· {pzemSensors.length} PZEM sensor</span>}
          </h3>
          {pzemSensors.length === 0 ? (
            <div className="text-sm text-muted-foreground italic h-[260px] flex items-center justify-center border border-dashed border-border rounded-md">
              Belum ada sensor PZEM — tambahkan di Sensor Manajemen (tipe: pzem).
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                {pzemSensors.map((s, idx) => (
                  <Line
                    key={s.id}
                    type="monotone"
                    dataKey={s.id}
                    name={s.name}
                    stroke={`hsl(var(--chart-${(idx % 5) + 1}))`}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
